import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import type { ParagraphProps } from 'antd/es/typography/Paragraph';
import { useState } from 'react';
import styled from 'styled-components';
const { Paragraph } = Typography;

const StyledParagraph = styled(Paragraph)`
  &.ant-typography {
    margin-bottom: 0;
    .anticon {
      font-size: 0.9em;
    }
  }
`;

export interface TableCellEllipsisProps
  extends Omit<ParagraphProps, 'ellipsis'> {
  /** Numero di righe visibili (prima dell'ellipsis) @default 2 */
  rows?: number;
  /** Larghezza massima della celle (max-width).
   *
   * Impostare il valore per attivare l'ellipsiss senza impostare quello della column. Se non è specificata, la larghezza è automatica e la cella si allarga per adattarsi al contenuto.
   * È possibile specificare solo la larghezza, la larghezza minima è fissata a width/2
   */
  width?: number;
  /** Indica di mostrare il testo nel Tooltips se attivo l'ellipsis @default true*/
  tooltip?: boolean;
  /** Indica se mostrare i pulsanti per espandere/contrarre il testo nascosto @default true */
  expandable?: boolean;
}

/**
 * Componente per visualizzare, all'interno di una cella di una tabella,
 * un testo con ellipsiss (estende Paragraph di antd).
 * @param width Larghezza massima della celle (max-width) - Impostare il valore per attivare l'ellipsiss senza impostare quello della column.
 * Se non è specificata, la larghezza è automatica e la cella si allarga per adattarsi al contenuto.
 * @param rows Numero di righe visibili (prima dell'ellipsis) - default 2
 * @param tooltip Indica di mostrare il testo nel Tooltips se attivo l'ellipsis - default true
 * @param expandable Indica se mostrare i pulsanti per espandere/contrarre il testo nascosto - default true
 */
export function TableCellEllipsis(props: TableCellEllipsisProps) {
  const {
    rows,
    width,
    tooltip = true,
    expandable = true,
    ...otherProps
  } = props;
  // Gestione pulsante "contrai" sul testo espanso
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={
        width ? { maxWidth: width, minWidth: width / 2 } : { minWidth: 60 }
      }
    >
      <p className="sr-only">{otherProps.children}</p>
      <StyledParagraph
        ellipsis={{
          rows: rows ?? 2,
          expandable: expandable ? 'collapsible' : false,
          expanded,
          onExpand: (_, info) => setExpanded(info.expanded),
          tooltip: tooltip,
          symbol: expanded =>
            !expanded ? (
              <PlusOutlined aria-hidden="true" />
            ) : (
              <MinusOutlined aria-hidden="true" />
            ),
        }}
        {...otherProps}
        // per lo screen reader nascondiamo l'elemento
        // e mostriamo il testo completo in un paragrafo nascosto sr-only
        aria-hidden="true"
      />
    </div>
  );
}
